import { useState } from 'react';
import { labToCssRgb, type DecorListItem, type LabColor } from '@pomoshnik/shared';

/**
 * Карточка декора в каталоге (Phase 6, Экран 2.2.2). Презентационный.
 *
 * Декор — это **поверхность (цвет/паттерн)**, не объект. Поэтому color swatch
 * (24×24 круг с реальным цветом из dominantLab) — критичная часть карточки,
 * наряду с photo-фоном.
 *
 * Three-tier visual fallback (от лучшего к худшему):
 *   1. dominantLab задан → swatch заливается `labToCssRgb(dominantLab)`.
 *      Если есть imageFilename — фото в верхней зоне, swatch отдельно.
 *      Если нет imageFilename — фон карточки = тот же color-block из dominantLab.
 *   2. dominantLab НЕ задан (B-022 — Slotex без dominantLab из-за отсутствия фото
 *      в источнике) → swatch = #cccccc + dashed border + aria-label
 *      «нет образца цвета». Карточка кликабельна, ведёт на DecorCard.
 *      Не скрываем такие SKU — это data quality signal владельцу базы.
 *
 * P-001: globalId передаётся в onItemClick **verbatim** (без encoding/normalize).
 * Encoding для URL делает Container через encodeURIComponent в navigate().
 */
export interface DecorResultCardProps {
  item: DecorListItem;
  /** Берётся из полной DecorSchema (`apiClient.decors.get`) при наличии. List
   *  endpoint в Phase 4 не возвращает dominantLab — Container'у придётся
   *  принять решение: либо опустить swatch, либо запросить отдельно. В Phase 6
   *  каталог использует FACADES_FIXTURE/COUNTERTOPS_FIXTURE напрямую через
   *  apiClient.decors.list — для каждой карточки `dominantLab` придёт
   *  optional через расширенный list endpoint в Phase 7+. Пока — Container
   *  передаёт undefined, и все swatch'и идут как B-022. Это OK для MVP.
   */
  dominantLab?: LabColor;
  /** Фото-2 (PR-DATA4): signed-URL фото декора из R2. null/undefined или onError
   *  → текущий плейсхолдер (инициал бренда / color-block из dominantLab). */
  imageUrl?: string | null;
  onItemClick: (globalId: string) => void;
}

const SWATCH_GREY = '#cccccc';

export function DecorResultCard({
  item,
  dominantLab,
  imageUrl,
  onItemClick,
}: DecorResultCardProps) {
  // Фото-2: при ошибке загрузки <img> откатываемся на плейсхолдер.
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = Boolean(imageUrl) && !imgFailed;
  const swatchColor = dominantLab ? labToCssRgb(dominantLab) : SWATCH_GREY;
  const swatchAriaLabel = dominantLab ? 'Образец цвета декора' : 'нет образца цвета';
  const swatchClassName = [
    'inline-block w-6 h-6 rounded-full shrink-0',
    dominantLab
      ? 'border-[1.5px] border-icon-border'
      : 'border-[1.5px] border-dashed border-icon-border',
  ].join(' ');

  // Image fallback chain (Phase 6 MVP — нет реальных R2 URLs):
  //   - есть primaryPhotoKey → placeholder с инициалом бренда (как Block 4)
  //   - есть dominantLab → image-area заливается тем же swatch-цветом
  //     (карточка визуально становится «образцом поверхности»)
  //   - иначе → нейтральный icon-bg + brand initial
  const imageBg = !item.primaryPhotoKey && dominantLab ? labToCssRgb(dominantLab) : undefined;

  return (
    <button
      type="button"
      onClick={() => onItemClick(item.globalId)}
      aria-label={`Открыть карточку: ${item.name}, ${item.brand}`}
      className={[
        'w-full rounded-xl bg-card border-[1.5px] border-accent shadow-card overflow-hidden',
        'text-left p-0 cursor-pointer',
        'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
      ].join(' ')}
    >
      {/* Image area: aspect-square ~280px на mobile-вьюпорте 320-360px */}
      <div
        className="w-full aspect-square bg-icon-bg flex items-center justify-center text-muted text-3xl font-display overflow-hidden"
        aria-hidden="true"
        style={!showImage && imageBg ? { background: imageBg } : undefined}
      >
        {showImage ? (
          // alt="" — декоративно: имя/бренд уже в aria-label кнопки.
          <img
            src={imageUrl as string}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : item.primaryPhotoKey ? (
          item.brand.charAt(0)
        ) : !dominantLab ? (
          '?'
        ) : null}
      </div>

      {/* Content area */}
      <div className="p-5 flex flex-col gap-2">
        <p className="font-body text-[18px] font-semibold text-ink m-0 line-clamp-2">{item.name}</p>

        <div className="flex items-center gap-3">
          <span
            aria-label={swatchAriaLabel}
            className={swatchClassName}
            style={{ background: swatchColor }}
          />
          <p className="text-muted text-base m-0">
            {item.brand} <span className="text-separator">•</span> {item.subcategory}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Badge>{item.subcategory}</Badge>
          {/* ADR-0047 (Вариант B): item.type — всегда `string[]`. Один бейдж на
              тип (мульти-тип столешниц: «Крапинка»/«Бетон»/«Песок»). */}
          {item.type.map((t) => (
            <Badge key={t}>{t}</Badge>
          ))}
        </div>
      </div>
    </button>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-sm px-3 py-1 rounded-full bg-icon-bg text-separator">{children}</span>
  );
}
